import React, { useEffect, useState } from "react";
import { Modal, ModalDialog } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postFetch } from "../../../../../../fetch/fetch";
import { C, toastOptions, formatComma, formatSigned, chipStyle } from "./giftcashutil";

/**
 * 기프티콘 캐쉬 관리표 > [+ 늘리기] — 선물하기 사이트에서 충전한 금액을 그대로 입력 (POST /dashboard/reviewEvent/cash/charge)
 * 처리 직원·시각은 서버에서 자동 기록
 * props: open, onClose, branch({key, label, balance}), onSaved
 */

const QUICK_AMOUNTS = [10000, 30000, 50000, 100000];

const GiftCashChargeModal: React.FC<any> = (props) => {

    const [amount, setAmount] = useState<number>(0);
    const [memo, setMemo] = useState<string>("");
    const [saving, setSaving] = useState<boolean>(false);

    useEffect(() => {
        if (props.open) {
            setAmount(0);
            setMemo("");
        }
    }, [props.open]);

    if (!props.branch) return null;

    const balance = +props.branch.balance || 0;
    const after = balance + amount;

    const handleAmountChange = (value: string) => {
        const digits = value.replace(/[^0-9]/g, "").slice(0, 9);
        setAmount(digits ? +digits : 0);
    };

    const handleSave = async () => {
        if (amount <= 0 || saving) return;
        try {
            setSaving(true);
            const result: any = await postFetch("/dashboard/reviewEvent/cash/charge", {
                branchKey: props.branch.key,
                amount,
                memo,
            });
            if (result.message === "success") {
                toast.success(`${props.branch.label} 캐쉬 ${formatComma(amount)}원 늘렸어요`, toastOptions);
                props.onSaved && props.onSaved();
                props.onClose();
            } else {
                console.log(result);
                toast.error(`저장 실패: ${result.message}`, toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("저장 중 오류가 발생했습니다", toastOptions);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal open={props.open} onClose={props.onClose}>
            <ModalDialog sx={{ width: 420, maxWidth: "calc(100vw - 32px)", p: 0, borderRadius: "12px", border: `1px solid ${C.border}`, overflow: "hidden", gap: 0, color: C.text }}>

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 20px", borderBottom: `1px solid ${C.line}` }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <div style={{ fontSize: 16, fontWeight: 700 }}>{props.branch.label} 캐쉬 늘리기</div>
                        <div style={{ fontSize: 12, color: C.gray }}>선물하기 사이트에서 충전한 금액을 그대로 입력</div>
                    </div>
                    <button type="button" aria-label="닫기" onClick={props.onClose} style={{ width: 32, height: 32, border: 0, background: "transparent", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                        <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={C.gray} strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"></path></svg>
                    </button>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: 18, padding: 20 }}>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: C.grayBg, borderRadius: 8, fontSize: 13 }}>
                        <span style={{ color: C.gray }}>현재 잔액</span>
                        <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: balance < 0 ? C.red : C.text }}>{formatSigned(balance)}원</span>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <label htmlFor="giftCashAmount" style={{ fontSize: 13, fontWeight: 500 }}>늘릴 금액</label>
                        <div style={{ display: "flex", alignItems: "center", height: 44, border: `1px solid ${C.blue}`, borderRadius: 8, padding: "0 14px", boxSizing: "border-box", gap: 8 }}>
                            <input
                                id="giftCashAmount"
                                type="text"
                                inputMode="numeric"
                                autoFocus
                                value={amount ? formatComma(amount) : ""}
                                placeholder="0"
                                onChange={(e) => handleAmountChange(e.target.value)}
                                style={{ flex: 1, border: 0, outline: 0, fontSize: 18, fontWeight: 700, fontFamily: "inherit", fontVariantNumeric: "tabular-nums", color: C.text, background: "transparent", minWidth: 0 }}
                            />
                            <span style={{ fontSize: 14, color: C.gray }}>원</span>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                            {QUICK_AMOUNTS.map((q) => (
                                <button key={q} type="button" onClick={() => setAmount(Math.min(amount + q, 999999999))} style={chipStyle(false)}>
                                    +{q / 10000}만
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <label htmlFor="giftCashMemo" style={{ fontSize: 13, fontWeight: 500 }}>메모 <span style={{ color: C.gray, fontWeight: 400 }}>(선택)</span></label>
                        <input
                            id="giftCashMemo"
                            type="text"
                            value={memo}
                            maxLength={255}
                            onChange={(e) => setMemo(e.target.value)}
                            placeholder="예: 10/27 비즈머니 충전 · 대구카드"
                            style={{ height: 40, border: `1px solid ${C.border}`, borderRadius: 8, padding: "0 14px", boxSizing: "border-box", fontSize: 13, fontFamily: "inherit", color: C.text, outline: 0 }}
                        />
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 2, fontSize: 13 }}>
                        <span style={{ color: C.gray }}>반영 후 잔액</span>
                        <span style={{ fontWeight: 700, fontVariantNumeric: "tabular-nums", color: after < 0 ? C.red : C.blue }}>{formatSigned(after)}원</span>
                    </div>
                </div>

                <div style={{ display: "flex", gap: 8, padding: "14px 20px", borderTop: `1px solid ${C.line}` }}>
                    <button type="button" onClick={props.onClose} style={{ flex: 1, height: 44, border: `1px solid ${C.border}`, background: "#FFFFFF", borderRadius: 8, fontSize: 14, fontFamily: "inherit", color: C.text, cursor: "pointer" }}>
                        취소
                    </button>
                    <button
                        type="button"
                        disabled={amount <= 0 || saving}
                        onClick={handleSave}
                        style={{ flex: 2, height: 44, border: 0, background: C.blue, opacity: amount <= 0 || saving ? 0.5 : 1, borderRadius: 8, fontSize: 14, fontWeight: 700, fontFamily: "inherit", color: "#FFFFFF", cursor: amount <= 0 || saving ? "default" : "pointer" }}
                    >
                        {saving ? "저장 중..." : `${formatComma(amount)}원 늘리기`}
                    </button>
                </div>
            </ModalDialog>
        </Modal>
    );
};

export default GiftCashChargeModal;
